import { Bed, Bath, Maximize, Trees, TrendingUp, ArrowUpRight, Percent, Sparkles, Radar, BarChart3, Globe, MousePointerClick, Music, Landmark, GraduationCap, Mountain, Store, MapPin, Camera } from "lucide-react";
import { LISTING, EQUITY_PROJECTION, INVEST_REASONS, REFI_SCENARIOS, REFI_IDEAS, HOW_WE_HELP, REDLANDS_ATTRACTIONS, REDLANDS_IMAGE } from "../data";
import { Reveal } from "./Reveal";
import { SellerGuide } from "./SellerGuide";

const HELP_ICONS = [Globe, Radar, MousePointerClick, BarChart3, Camera, MapPin];
const ATTRACTION_ICONS = { Store, Music, Landmark, GraduationCap, Trees, Mountain };

export const Listings = () => (
  <section id="listings" data-testid="listings-section" className="bg-white">
    <SellerGuide />
    {/* Featured listing hero */}
    <div className="relative">
      <div className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <img src={LISTING.hero} alt={LISTING.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full">
            <span className="inline-block bg-gold text-white eyebrow text-[11px] px-4 py-2 mb-5" data-testid="listing-status">{LISTING.status}</span>
            <h2 className="font-heading font-light text-white text-4xl sm:text-6xl tracking-tight leading-none">{LISTING.name}</h2>
            <p className="text-white/85 font-light text-lg mt-3">{LISTING.address}</p>
            <p className="font-heading text-gold text-3xl sm:text-4xl mt-5" data-testid="listing-price">{LISTING.price}</p>
          </div>
        </div>
      </div>
    </div>

    {/* Details + gallery */}
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow text-gold mb-4">The Listing</p>
            <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight">A fabulous home, a smarter investment</h3>
            <p className="text-[#52525b] font-light text-lg mt-6 leading-relaxed">{LISTING.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-black/5 mt-10">
              {[
                { icon: Bed, label: `${LISTING.beds} Beds` },
                { icon: Bath, label: `${LISTING.baths} Baths` },
                { icon: Maximize, label: `${LISTING.sqft} sqft` },
                { icon: Trees, label: LISTING.lot }
              ].map((s) => (
                <div key={s.label} className="bg-white p-5 flex flex-col items-center text-center">
                  <s.icon className="text-gold" size={22} strokeWidth={1.5} />
                  <span className="text-sm mt-2 font-light">{s.label}</span>
                </div>
              ))}
            </div>

            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-10">
              {LISTING.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#0a0a0a] font-light">
                  <Sparkles size={15} className="text-gold shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-5 grid gap-4 content-start">
          {LISTING.gallery.map((src, i) => (
            <Reveal key={src} delay={i * 0.1}>
              <div className="overflow-hidden group">
                <img src={src} alt={`${LISTING.name} interior ${i + 1}`} className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    {/* What to do in Redlands */}
    <div data-testid="explore-redlands" className="bg-white border-t border-black/5 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -bottom-5 -right-5 w-full h-full border border-gold/40 hidden sm:block" />
              <img src={REDLANDS_IMAGE} alt="Downtown Redlands, California" className="relative w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="text-gold" size={22} />
                <p className="eyebrow text-gold">Neighborhood Lifestyle Guide</p>
              </div>
              <h3 className="font-heading text-3xl sm:text-5xl font-light leading-tight">Life around The Crestview Estate</h3>
              <p className="text-[#52525b] font-light text-lg mt-6 leading-relaxed max-w-2xl">
                For every listing, we publish a curated local guide — the best restaurants, parks,
                schools, and things to do nearby — so buyers can instantly picture their life in the
                area. Here's a taste of the Redlands lifestyle that comes with this home.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {REDLANDS_ATTRACTIONS.map((a, i) => {
            const Icon = ATTRACTION_ICONS[a.icon] || Sparkles;
            return (
              <Reveal key={a.title} delay={i * 0.07}>
                <div data-testid={`redlands-attraction-${i}`} className="bg-surface border border-black/5 p-8 h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                    <Icon className="text-gold" size={22} strokeWidth={1.5} />
                  </div>
                  <h4 className="font-heading text-xl mt-6">{a.title}</h4>
                  <p className="text-[#52525b] font-light text-sm mt-3 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>

    {/* Equity projection */}
    <div className="bg-surface py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="text-gold" size={22} />
            <p className="eyebrow text-gold">Equity Created</p>
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight max-w-2xl">Your equity in the near future</h3>
          <p className="text-[#52525b] font-light text-lg mt-5 max-w-2xl">
            A look at how ownership could build wealth over five years — through appreciation and loan paydown combined.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white border border-black/5 overflow-x-auto mt-10">
            <table className="w-full text-left" data-testid="equity-table">
              <thead>
                <tr className="eyebrow text-[10px] text-[#a1a1aa] border-b border-black/10">
                  <th className="px-6 py-5 font-medium">Timeline</th>
                  <th className="px-6 py-5 font-medium">Est. Home Value</th>
                  <th className="px-6 py-5 font-medium">Loan Balance</th>
                  <th className="px-6 py-5 font-medium">Your Equity</th>
                </tr>
              </thead>
              <tbody>
                {EQUITY_PROJECTION.map((r, i) => (
                  <tr key={r.year} className={`border-b border-black/5 last:border-0 ${i === EQUITY_PROJECTION.length - 1 ? "bg-gold/5" : ""}`}>
                    <td className="px-6 py-5 font-medium">{r.year}</td>
                    <td className="px-6 py-5 text-[#52525b]">{r.value}</td>
                    <td className="px-6 py-5 text-[#52525b]">{r.loan}</td>
                    <td className="px-6 py-5 font-heading text-xl text-gold">{r.equity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#a1a1aa] font-light mt-4">
            Illustrative estimates based on 20% down, a ~6.5% 30-yr loan, and 4% annual appreciation. Not financial advice — actual results vary.
          </p>
        </Reveal>
      </div>
    </div>

    {/* Reasons to invest */}
    <div className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="eyebrow text-gold mb-4">Why Invest</p>
          <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight max-w-2xl">Reasons to invest — and the upside</h3>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {INVEST_REASONS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <div data-testid={`invest-reason-${i}`} className="bg-surface border border-black/5 p-8 h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <ArrowUpRight className="text-gold" size={24} />
                <h4 className="font-heading text-2xl mt-5">{r.title}</h4>
                <p className="inline-flex items-center gap-2 text-sm text-gold mt-3 font-medium">{r.outcome}</p>
                <p className="text-[#52525b] font-light text-sm mt-3 leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    {/* Refinancing */}
    <div className="bg-[#0a0a0a] text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <Percent className="text-gold" size={22} />
            <p className="eyebrow text-gold">Refinancing Ideas</p>
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight max-w-2xl">A better rate down the road</h3>
          <p className="text-white/70 font-light text-lg mt-5 max-w-2xl">
            Buy now, and refinance later if rates improve. Here's how much a future rate drop could save on this loan.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white/5 border border-white/10 overflow-x-auto mt-10">
            <table className="w-full text-left" data-testid="refi-table">
              <thead>
                <tr className="eyebrow text-[10px] text-white/50 border-b border-white/10">
                  <th className="px-6 py-5 font-medium">Future Rate</th>
                  <th className="px-6 py-5 font-medium">New Monthly P&amp;I</th>
                  <th className="px-6 py-5 font-medium">Monthly Savings</th>
                  <th className="px-6 py-5 font-medium">Annual Savings</th>
                </tr>
              </thead>
              <tbody>
                {REFI_SCENARIOS.map((r, i) => (
                  <tr key={r.rate} className={`border-b border-white/10 last:border-0 ${i === 0 ? "text-white/60" : ""}`}>
                    <td className="px-6 py-5 font-medium">{r.rate}</td>
                    <td className="px-6 py-5">{r.payment}</td>
                    <td className="px-6 py-5">{r.monthly}</td>
                    <td className="px-6 py-5 font-heading text-xl text-gold">{r.annual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-white/40 font-light mt-4">
            Estimates on a $1,036,000 loan, 30-yr term. Savings depend on closing costs, credit, and market rates at the time of refinance.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-6 mt-12">
          {REFI_IDEAS.map((idea, i) => (
            <Reveal key={idea.title} delay={i * 0.08}>
              <div data-testid={`refi-idea-${i}`} className="border border-white/10 p-7 h-full hover:bg-white/[0.03] transition-colors">
                <h4 className="font-heading text-xl">{idea.title}</h4>
                <p className="text-white/60 font-light text-sm mt-3 leading-relaxed">{idea.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 text-center">
            <a href="#quiz" data-testid="listings-cta" className="inline-flex items-center gap-2 bg-gold text-white text-sm px-9 py-4 hover:bg-gold-hover transition-colors tracking-wide">
              Talk Strategy With Melissa
            </a>
          </div>
        </Reveal>
      </div>
    </div>
    {/* Here's how we can help */}
    <div data-testid="how-we-help" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="eyebrow text-gold mb-4">Our Advantage</p>
          <h3 className="font-heading text-3xl sm:text-5xl font-light leading-tight max-w-3xl">Here's how we can help</h3>
          <p className="text-[#52525b] font-light text-lg mt-6 max-w-2xl">
            We pair Melissa's market expertise with advanced technology that connects buyers directly to your home —
            target-driven through analytics, so the right people see your listing at the right moment.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {HOW_WE_HELP.map((h, i) => {
            const Icon = HELP_ICONS[i];
            return (
              <Reveal key={h.title} delay={i * 0.08}>
                <div data-testid={`how-we-help-${i}`} className="bg-surface border border-black/5 p-8 h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                    <Icon className="text-gold" size={22} strokeWidth={1.5} />
                  </div>
                  <h4 className="font-heading text-xl mt-6">{h.title}</h4>
                  <p className="text-[#52525b] font-light text-sm mt-3 leading-relaxed">{h.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between border-t border-black/5 pt-10">
            <p className="font-heading text-2xl sm:text-3xl font-light max-w-xl">Ready to put this technology to work for you?</p>
            <a href="#contact" data-testid="how-we-help-cta" className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-sm px-9 py-4 hover:bg-[#27272a] transition-colors tracking-wide whitespace-nowrap">
              Get In Touch
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
